import { Injectable, inject } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';
import { PlatformGraphQlService } from '../graphql/platform-graphql.service';
import { BonusStatus, BonusStatusLabel, BonusType } from '../user/data/user-bonuses.data';
import { UserService } from '../user/user.service';


@Injectable({
  providedIn: 'root',
})
export class LootboxService {

  private _platformApi = inject(PlatformGraphQlService);
  private _user = inject(UserService);

  private _isBonusListEmpty: boolean = null;

  private _bonusStatusListForShow = [
    BonusStatus.NEW,
    BonusStatus.OPENING_EXPIRED,
  ];

  public lootboxList$ = new ReplaySubject<any[]>(1);

  public get isCurrentListEmpty(): boolean {
    return this._isBonusListEmpty;
  }

  /**
   * Load list of player money bonuses
   */
  public loadLootboxList() {
    this._platformApi.lootboxList(true).pipe(
      first(),
      map((res: any) => res?.data?.result?.info?.player?.lootboxes?.lootboxList?.map(e => e?.lootbox) || []),
      map((list: any[]) => this._mapBonuses(list || [])),
      map((list: any[]) => this._filterBonusesByStatus(list || [])),
      tap((list: any[]) => {
        this._isBonusListEmpty = !list.filter(b => this._bonusStatusListForShow.includes(b.status)).length;
      }),
      catchError(error => of([])),
      tap(list => this.lootboxList$.next(list)),
    ).subscribe();
  }

  public loadOriginalLootboxList$() {
    return this._platformApi.lootboxList(true).pipe(
      first(),
      map((res: any) => res?.data?.result?.info?.player?.lootboxes?.lootboxList?.map(e => e?.lootbox) || []),
      map((list: any[]) => this._mapBonuses(list || [])),
    );
  }

  /**
   * Prepare bonus list for using in frontend
   * @param list
   * @private
   */
  private _mapBonuses(list: any[]): any[] {
    list = list
      .map(bonus => {
        bonus = {
          ...bonus,
          type: BonusType.LOOTBOX,
          active: ![
            BonusStatus.OPENED,
          ].includes(bonus.status),
          activatable: [BonusStatus.NEW].includes(bonus.status),
          cancelable: [
            BonusStatus.NEW,
          ].includes(bonus.status),
          expired: [BonusStatus.OPENING_EXPIRED].includes(bonus.status),
          statusLabel: BonusStatusLabel[bonus.status] || bonus.status,
          issuedAt: bonus.issuedAt ? new Date(bonus.issuedAt) : null,
          expiredAt: bonus.expiresAt ? new Date(bonus.expiresAt) : null,
        };
        return bonus;
      })
      .filter(bonus => !this._isBonusExpired(bonus));
    return list;
  }

  private _isBonusExpired(bonus: any) {
    const usageExpirationAfter = new Date(bonus.expiresAt);
    const currentDate = new Date();
    const extendedExpirationDate = new Date(usageExpirationAfter);
    extendedExpirationDate.setDate(extendedExpirationDate.getDate() + 7);
    return extendedExpirationDate < currentDate;
  }

  private _filterBonusesByStatus(list: any[]) {
    list = list.filter(bonus => this._bonusStatusListForShow.includes(bonus.status));
    return list;
  }

  /**
   * Activate loot box by id
   * @param id
   */
  public activateLootBox(id: string) {
    return this._platformApi.openLootbox(id, this._user.currentWallet.id);
  }

  /**
   * Cancel lootbox
   * @param id
   */
  public cancelLootbox(id) {
    return this._platformApi.cancelLootbox(id);
  }

}
